import React, { FC, ReactNode } from 'react';
import classNames from 'classnames';
import { Button, Input } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import TrashIcon from 'icons/Trash.icon';
import RefreshIcon from 'icons/Refresh.icon';
import CheckIcon from 'icons/Check.icon';
import ClockIcon from 'icons/ArrowClock.icon';
import SortIcon from 'icons/Sort.icon';
import { getIsArrayEmpty } from 'utils';
import usePastTime from 'hooks/usePastTime';
import SearchIcon from 'icons/Search.icon';
import ChecklistIcon from 'icons/Checklist.icon';
import {
  DataRoomTermsAndConditions,
  DataRoomTermsAndConditionsSort,
} from 'router/subrouters/DataRoom/models/DataRoomTermsAndConditions';

import './UsersTCPanel.styles.scss';

type UsersTCPanelProps = {
  className?: string;
  items: DataRoomTermsAndConditions[];
  deletedCount: number;
  sort: DataRoomTermsAndConditionsSort;
  search: string;
  isShowingDeleted: boolean;
  selectedId: string | null;
  onSortChange: (sort: DataRoomTermsAndConditionsSort) => void;
  onSearchChange: (search: string) => void;
  onToggleDeleted: () => void;
  onSelect: (item: DataRoomTermsAndConditions) => void;
  onClear: () => void;
  onDelete: (id: string) => void;
  onRestore: (id: string) => void;
};

type SortOption = {
  value: DataRoomTermsAndConditionsSort;
  icon: ReactNode;
  label: string;
};

const SORT_OPTIONS: SortOption[] = [
  { value: 'recent', icon: <ClockIcon />, label: 'recent' },
  { value: 'a-z', icon: <SortIcon className="asc" />, label: 'aToZ' },
  { value: 'z-a', icon: <SortIcon className="desc" />, label: 'zToA' },
];

const UsersTCPanel: FC<UsersTCPanelProps> = (props) => {
  const {
    className,
    items,
    deletedCount,
    sort,
    search,
    isShowingDeleted,
    selectedId,
    onSortChange,
    onSearchChange,
    onToggleDeleted,
    onSelect,
    onClear,
    onDelete,
    onRestore,
  } = props;

  const getPastTime = usePastTime();

  const { t } = useTranslation();

  return (
    <div className={classNames('users-tc-panel', className)}>
      <Input
        placeholder={t('searchTemplates')}
        prefixNode={<SearchIcon />}
        value={search}
        onChange={({ target: { value } }) => onSearchChange(value)}
      />

      <div className="users-tc-panel__controls">
        <div>
          {SORT_OPTIONS.map(({ value, icon, label }) => (
            <Button
              key={value}
              variant="outline"
              icon={icon}
              className={classNames({
                active: sort === value,
              })}
              onClick={() => onSortChange(value)}
            >
              {t(label)}
            </Button>
          ))}

          {selectedId && !isShowingDeleted && (
            <Button variant="link" onClick={onClear}>
              {t('clear')}
            </Button>
          )}
        </div>

        <Button
          variant="link"
          icon={isShowingDeleted ? ChecklistIcon : TrashIcon}
          onClick={onToggleDeleted}
        >
          {isShowingDeleted
            ? t('active')
            : t('deletedCount', { count: deletedCount })}
        </Button>
      </div>

      <div className="users-tc-panel__list">
        {getIsArrayEmpty(items) && (
          <p className="empty">
            {t(search ? 'noResultsFor' : 'noTemplates', { search })}
          </p>
        )}

        {items.map((item) => {
          const { _id, name, updatedAt } = item || {};

          const isSelected = _id === selectedId;

          return (
            <div
              key={_id}
              className={classNames('users-tc-panel__item', {
                'users-tc-panel__item--selected':
                  isSelected && !isShowingDeleted,
                'users-tc-panel__item--deleted': isShowingDeleted,
              })}
              onClick={() => !isShowingDeleted && onSelect(item)}
            >
              <div className="users-tc-panel__item-left">
                {isShowingDeleted ? null : (
                  <span className="users-tc-panel__check">
                    {isSelected ? <CheckIcon /> : null}
                  </span>
                )}
                <span className="users-tc-panel__item-name">{name}</span>
              </div>

              <div className="users-tc-panel__item-right">
                <span className="users-tc-panel__item-date">
                  {getPastTime(updatedAt)}
                </span>

                <Button
                  variant="icon"
                  icon={isShowingDeleted ? RefreshIcon : TrashIcon}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isShowingDeleted) {
                      onRestore(_id);
                    } else {
                      onDelete(_id);
                    }
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UsersTCPanel;
